import { useRef, useState, useCallback } from 'react'

interface SpeechMessage {
    type: 'speech'
    data?: string
    completed?: boolean
    error?: string
}

const SAMPLE_RATE = 16000 // 16 kHz for speech
const CHANNELS = 1

// How much audio to accumulate before starting/resuming playback. Larger
// values are more resilient to network jitter at the cost of latency to
// first sound.
const PREBUFFER_SECONDS = 0.25
// Small lead time so the first scheduled chunk in a flush isn't clamped to
// "now" by the time source.start() actually executes.
const SCHEDULE_LEAD_SECONDS = 0.05

type PlaybackState = 'idle' | 'buffering' | 'streaming'

const decodeChunk = (base64Data: string): Float32Array | null => {
    try {
        const binaryString = atob(base64Data)

        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
        }

        const evenLength = bytes.length - (bytes.length % 2)
        if (bytes.length % 2 !== 0) {
            console.warn('[AudioPlayback] Odd byte length, dropping last byte', {
                original: bytes.length,
                adjusted: evenLength,
            })
        }

        const alignedBuffer = new ArrayBuffer(evenLength)
        const alignedBytes = new Uint8Array(alignedBuffer)
        alignedBytes.set(bytes.slice(0, evenLength))

        const int16Array = new Int16Array(alignedBuffer)
        const float32Chunk = new Float32Array(int16Array.length)

        for (let i = 0; i < int16Array.length; i++) {
            float32Chunk[i] = int16Array[i] / 32768.0
        }

        return float32Chunk
    } catch (err) {
        console.error('[AudioPlayback] Failed to decode audio chunk:', err)
        return null
    }
}

export const usePlaybackAudioStream = () => {
    const [isPlayingAudio, setIsPlayingAudio] = useState(false)

    const audioContextRef = useRef<AudioContext | null>(null)
    const playbackStateRef = useRef<PlaybackState>('idle')
    const pendingChunksRef = useRef<Float32Array[]>([])
    const pendingDurationRef = useRef(0)
    const nextStartTimeRef = useRef(0)
    const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())

    const initAudioContext = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window['webkitAudioContext'])({
                sampleRate: SAMPLE_RATE,
            })
        }
        return audioContextRef.current
    }, [])

    const stopPlayback = useCallback(() => {
        activeSourcesRef.current.forEach((source) => {
            try {
                source.onended = null
                source.stop()
            } catch (e) {
                console.error('[AudioPlayback] Error stopping audio source:', e)
            }
        })
        activeSourcesRef.current.clear()
        pendingChunksRef.current = []
        pendingDurationRef.current = 0
        nextStartTimeRef.current = 0
        playbackStateRef.current = 'idle'
        setIsPlayingAudio(false)
    }, [])

    const scheduleChunk = useCallback(
        (chunk: Float32Array) => {
            const audioContext = initAudioContext()
            const audioBuffer = audioContext.createBuffer(CHANNELS, chunk.length, SAMPLE_RATE)
            audioBuffer.getChannelData(0).set(chunk)

            const source = audioContext.createBufferSource()
            source.buffer = audioBuffer
            source.connect(audioContext.destination)

            const startTime = nextStartTimeRef.current
            source.onended = () => {
                activeSourcesRef.current.delete(source)
                if (
                    activeSourcesRef.current.size === 0 &&
                    pendingChunksRef.current.length === 0 &&
                    playbackStateRef.current !== 'buffering'
                ) {
                    playbackStateRef.current = 'idle'
                    setIsPlayingAudio(false)
                }
            }

            activeSourcesRef.current.add(source)
            source.start(startTime)
            nextStartTimeRef.current = startTime + audioBuffer.duration
            setIsPlayingAudio(true)
        },
        [initAudioContext],
    )

    const flushPending = useCallback(() => {
        if (pendingChunksRef.current.length === 0) return

        const audioContext = initAudioContext()
        nextStartTimeRef.current = audioContext.currentTime + SCHEDULE_LEAD_SECONDS
        playbackStateRef.current = 'streaming'

        console.info('[AudioPlayback] Starting playback', {
            bufferedSeconds: pendingDurationRef.current.toFixed(2),
            chunks: pendingChunksRef.current.length,
        })

        const chunks = pendingChunksRef.current
        pendingChunksRef.current = []
        pendingDurationRef.current = 0
        chunks.forEach(scheduleChunk)
    }, [initAudioContext, scheduleChunk])

    const handleSpeechMessage = useCallback(
        (msg: SpeechMessage) => {
            if (msg.error) {
                console.error('[AudioPlayback] Speech error:', msg.error)
                stopPlayback()
                return
            }

            if (msg.completed) {
                // No more chunks coming right now — start playback of
                // whatever we have rather than waiting for the prebuffer
                // threshold, which may never be reached for short sentences.
                if (playbackStateRef.current === 'buffering') {
                    flushPending()
                }
                console.info('[AudioPlayback] Sentence complete')
                return
            }

            if (msg.data) {
                const chunk = decodeChunk(msg.data)
                if (!chunk || chunk.length === 0) return

                const audioContext = initAudioContext()

                if (playbackStateRef.current === 'streaming') {
                    if (nextStartTimeRef.current < audioContext.currentTime) {
                        console.warn('[AudioPlayback] Playback underrun, rebuffering', {
                            behindBy: (audioContext.currentTime - nextStartTimeRef.current).toFixed(
                                2,
                            ),
                        })
                        playbackStateRef.current = 'buffering'
                        pendingChunksRef.current.push(chunk)
                        pendingDurationRef.current += chunk.length / SAMPLE_RATE
                    } else {
                        scheduleChunk(chunk)
                    }
                    return
                }

                pendingChunksRef.current.push(chunk)
                pendingDurationRef.current += chunk.length / SAMPLE_RATE
                playbackStateRef.current = 'buffering'

                if (pendingDurationRef.current >= PREBUFFER_SECONDS) {
                    flushPending()
                }
            }
        },
        [flushPending, initAudioContext, scheduleChunk, stopPlayback],
    )

    return {
        isPlayingAudio,
        handleSpeechMessage,
        stopPlayback,
    }
}
